import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { allVideos } from '@/mocks/videos';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === slug) || categories[0],
    [slug]
  );

  const categoryVideos = useMemo(
    () => allVideos.filter((v) => v.categorySlug === activeCategory.slug),
    [activeCategory.slug]
  );

  const totalPages = Math.ceil(categoryVideos.length / ITEMS_PER_PAGE);
  const displayedVideos = categoryVideos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${activeCategory.name} - 白云视频高清在线观看 | 97视频丝瓜视频同类`}
        description={`白云视频${activeCategory.name}频道，汇聚最新最全${activeCategory.name}资源，${activeCategory.count}部精选内容免费在线观看。白云视频与97视频、丝瓜视频同类型高清影视平台。`}
        keywords={`白云视频,${activeCategory.name},97视频,丝瓜视频,高清${activeCategory.name},在线观看,免费${activeCategory.name}`}
        canonicalUrl={`https://www.baiyunvideo.com/category/${activeCategory.slug}`}
      />

      <Navbar />

      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeCategory.name },
              ]}
            />
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">
              共 {categoryVideos.length} 部 {activeCategory.name} 资源 · 高清在线观看
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Categories */}
            <aside className="w-full lg:w-[200px] flex-shrink-0">
              <h3 className="text-sm font-semibold text-foreground-800 mb-3">全部分类</h3>
              <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory.slug === cat.slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    <span>{cat.name}</span>
                    <span className={`text-xs ml-auto ${activeCategory.slug === cat.slug ? 'text-primary-500' : 'text-foreground-400'}`}>
                      {cat.count}
                    </span>
                  </Link>
                ))}
              </div>
            </aside>

            {/* Video Grid */}
            <div className="flex-1 min-w-0">
              {displayedVideos.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {displayedVideos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-10">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                            currentPage === page
                              ? 'bg-primary-500 text-background-50 font-medium'
                              : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-movie-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}